import type { AppProps } from 'next/app'
import { ScreenClassProvider, setConfiguration } from 'react-grid-system'
import { useRouter } from 'next/router'
import { fireAnalyticsEvent, getGtmClickSource } from '~analyticsUtils'
import { breakpoints } from 'utils/styles/breakpoints'
import { useEffect, useRef } from 'react'
import { INDButton } from '~elements'
import { BreakpointKey } from 'types'
import 'styles/dist/tokens.css'
import 'styles/globals.scss'
import { ModalProvider } from 'lib/context/modalContext'

const breakpointVars = Object.keys(breakpoints).map(key => parseInt(breakpoints[key as BreakpointKey].toString()))

const containerWidths = Object.entries(breakpoints).map(([key, value]) => {
  const fullWidthBreakpoints = ['xs', 'sm', 'sm-minus']
  if (fullWidthBreakpoints.includes(key)) {
    return '100%'
  }

  return value - 48 // Subtract 48px for gutters
})

setConfiguration({
  breakpointVars,
  // @ts-ignore: 100% string above is required
  containerWidths,
  gutterWidth: 32,
  maxScreenClass: 'xl'
})

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const previousPathRef = useRef<string>(typeof window !== 'undefined' ? window.location.pathname : '')
  const hasFiredInitialPageview = useRef(false)

  const handleRouteChangeComplete = (url: string) => {
    const nextPath = new URL(url, window.location.origin).pathname
    const prevPath = previousPathRef.current

    // Do not fire new pageview when Calendar adds querystrings to route
    if (prevPath === nextPath && hasFiredInitialPageview.current) {
      return
    }

    const gtmPageViewData = {
      event: 'pageview',
      clickPreviousPv: previousPathRef.current.includes('shows') ? 'shows' : 'other_events',
      eventData: { eventType: 'other_events' },
      pageType: getGtmClickSource(url)
    }

    fireAnalyticsEvent(null, url, gtmPageViewData)

    previousPathRef.current = nextPath
    hasFiredInitialPageview.current = true
  }

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    // Fire once on first load
    if (router.isReady && !hasFiredInitialPageview.current) {
      handleRouteChangeComplete(router.asPath)
    }

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [router.isReady, handleRouteChangeComplete])

  return (
    <ScreenClassProvider>
      <ModalProvider>
        <INDButton />
        <div id='root-modal' />
        <Component {...pageProps} />
        <div style={{ display: 'none' }}>build number {process.env.NEXT_PUBLIC_BUILD_NUMBER}</div>
      </ModalProvider>
    </ScreenClassProvider>
  )
}

export default App
